import { progressReportDateForInput } from "./progressReportUtils";

function plainSubdoc(obj) {
  if (obj == null) return {};
  if (typeof obj.toObject === "function") return obj.toObject();
  return { ...(typeof obj === "object" ? obj : {}) };
}

/** Build API payload for one edited report row vs its baseline. */
export function buildProgressReportSavePayload(row, baseline, trade) {
  if (!trade?._id || !baseline) return null;
  const ptPrev = plainSubdoc(trade.progressTracker);
  const progressTracker = { ...ptPrev };
  let ptChanged = false;

  const ptFields = ["action", "notes", "tradeCategory", "complianceReport"];
  for (const field of ptFields) {
    const rowKey =
      field === "complianceReport" ? "compliance" : field;
    const a = String(row[rowKey] ?? "").trim();
    const b = String(baseline[rowKey] ?? "").trim();
    if (a !== b) {
      progressTracker[field] = row[rowKey] ?? "";
      ptChanged = true;
    }
  }

  const followA = progressReportDateForInput(row.followUpDate);
  const followB = progressReportDateForInput(baseline.followUpDate);
  if (followA !== followB) {
    progressTracker.followUpDate = followA || "";
    ptChanged = true;
  }

  if (!ptChanged) return null;
  return { progressTracker };
}

export function getDirtyProgressReportRows(rows, baselineRows) {
  const baselineByKey = new Map(
    (baselineRows || []).map((r) => [r.rowKey, r]),
  );
  return (rows || []).filter((row) => {
    const base = baselineByKey.get(row.rowKey);
    if (!base) return true;
    return PROGRESS_REPORT_ROW_KEYS.some(
      (k) => String(row[k] ?? "").trim() !== String(base[k] ?? "").trim(),
    );
  });
}

const PROGRESS_REPORT_ROW_KEYS = [
  "tradeCategory",
  "compliance",
  "action",
  "followUpDate",
  "notes",
];

function isValidTradeObjectId(id) {
  return /^[a-f0-9]{24}$/i.test(String(id || "").trim());
}

/** Persist all dirty rows; returns count of rows actually written. */
export async function saveProgressReportRows(
  rows,
  baselineRows,
  trades,
  axiosInstance,
) {
  const tradeById = new Map(
    (trades || [])
      .filter((t) => t._id != null)
      .map((t) => [String(t._id), t]),
  );
  const baselineByKey = new Map(
    (baselineRows || []).map((r) => [r.rowKey, r]),
  );
  const dirty = getDirtyProgressReportRows(rows, baselineRows);
  let savedCount = 0;
  let skippedInvalid = 0;

  for (const row of dirty) {
    const tradeId = String(row.tradeId || "").trim();
    if (!isValidTradeObjectId(tradeId)) {
      skippedInvalid += 1;
      continue;
    }
    const trade = tradeById.get(tradeId);
    const baseline = baselineByKey.get(row.rowKey);
    const payload = buildProgressReportSavePayload(row, baseline, trade);
    if (!payload) continue;

    await axiosInstance.put(`/trades/${tradeId}`, payload);
    savedCount += 1;
  }

  return { dirtyCount: dirty.length, savedCount, skippedInvalid };
}
